import { useState } from "react";
import {
  EditBase,
  Form,
  useNotify,
  useRecordContext,
  useTranslate,
  type Identifier,
} from "ra-core";
import { Button } from "@/components/ui/button";
import { DeleteButton } from "@/components/admin/delete-button";
import { SaveButton } from "@/components/admin/form";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import type { Task } from "../types";
import { TaskDetailView } from "./TaskDetailView";
import { TaskFormContent } from "./TaskFormContent";
import { TaskMeetingPlayback } from "./TaskMeetingPlayback";

const TaskEditContent = ({
  close,
  startInEdit,
}: {
  close: () => void;
  startInEdit?: boolean;
}) => {
  const notify = useNotify();
  const translate = useTranslate();
  const task = useRecordContext<Task>();
  // Zoom-sourced tasks render an inline player + transcript, so give them more room.
  const isZoom = !!task?.zoom_meeting_id;
  // View-first: open read-only, toggle to inputs on Edit. The <Form> stays
  // mounted in both modes so SaveButton keeps its context.
  const [editing, setEditing] = useState(!!startInEdit);

  return (
    <DialogContent
      className={`${
        isZoom ? "lg:max-w-2xl" : "lg:max-w-xl"
      } overflow-y-auto max-h-9/10 top-1/20 translate-y-0`}
    >
      <Form className="flex flex-col gap-4">
        <DialogHeader>
          <DialogTitle>
            {editing ? translate("resources.tasks.action.edit") : "Task"}
          </DialogTitle>
        </DialogHeader>
        {editing ? (
          <TaskFormContent showSales showDone showContact />
        ) : (
          <TaskDetailView />
        )}
        <TaskMeetingPlayback />
        <DialogFooter className="w-full sm:justify-between gap-4">
          <DeleteButton
            mutationOptions={{
              onSuccess: () => {
                close();
                notify("resources.tasks.deleted", {
                  type: "info",
                  undoable: true,
                });
              },
            }}
            redirect={false}
          />
          {editing ? (
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditing(false)}
              >
                {translate("ra.action.cancel")}
              </Button>
              <SaveButton label="ra.action.save" />
            </div>
          ) : (
            <Button type="button" onClick={() => setEditing(true)}>
              {translate("ra.action.edit")}
            </Button>
          )}
        </DialogFooter>
      </Form>
    </DialogContent>
  );
};

export const TaskEdit = ({
  open,
  close,
  taskId,
  startInEdit,
}: {
  taskId: Identifier;
  open: boolean;
  close: () => void;
  startInEdit?: boolean;
}) => {
  const notify = useNotify();
  return (
    <Dialog open={open} onOpenChange={close}>
      {open && taskId && (
        <EditBase
          id={taskId}
          resource="tasks"
          className="mt-0"
          mutationOptions={{
            onSuccess: () => {
              close();
              notify("resources.tasks.updated", {
                type: "info",
                undoable: true,
              });
            },
          }}
          redirect={false}
        >
          <TaskEditContent close={close} startInEdit={startInEdit} />
        </EditBase>
      )}
    </Dialog>
  );
};
